import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, Space, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { useCreateWorkOrderTemplateMutation, useDeleteWorkOrderTemplateMutation, useGetWorkOrderTemplateByIdQuery, useGetWorkOrderTemplatesQuery, useUpdateWorkOrderTemplateMutation } from "../../api/workordertemplate.api";
import { ListWorkOrderTemplateDto, WorkOrderTemplateDto, MutableWorkOrderTemplateDto, WorkOrderAssignmentsDto } from "../../models/workordertemplate.models";
import { WorkOrderTemplateList } from "../../Components/WorkOrderTemplates/WorkOrderTemplateList";
import { WorkOrderTemplateDetailView } from "../../Components/WorkOrderTemplates/WorkOrderTemplateDetailView";
import { WorkOrderTemplateEditView } from "../../Components/WorkOrderTemplates/WorkOrderTemplateEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";
import { useGetWorkOrderByIdQuery } from "../../api/workorder.api";

const WorkOrderTemplatesPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ workOrderTemplateId?: string, workOrderId?: string }>();
	const isEditing = useMatch("/workordertemplates/:workOrderTemplateId/edit") !== null;
	const isNew = params.workOrderTemplateId === "new";
	const templateIdFromParams = params.workOrderTemplateId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const isNewFromWorkOrder = useMatch("/workordertemplates/:workOrderTemplateId/workorder/:workOrderId") !== null;
	const { data: workOrder } = useGetWorkOrderByIdQuery(isNewFromWorkOrder ? params.workOrderId || null : null);

	const { data: templates, isPending: isLoadingTemplates } = useGetWorkOrderTemplatesQuery();
	const [selectedTemplate, setSelectedTemplate] = useState<ListWorkOrderTemplateDto | null>(null);
	const { data: template, isLoading } = useGetWorkOrderTemplateByIdQuery(selectedTemplate?.WorkOrderTemplateId || templateIdFromParams || null);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedTemplateId, setRequestedTemplateId] = useState<string | null>(null);

	const { mutate: createTemplate, isPending: isCreating } = useCreateWorkOrderTemplateMutation();
	const { mutate: updateTemplate, isPending: isUpdating } = useUpdateWorkOrderTemplateMutation();
	const { mutate: deleteTemplate } = useDeleteWorkOrderTemplateMutation();

	const isSaving = isCreating || isUpdating;


	const workOrderTemplate: WorkOrderTemplateDto | null = isNewFromWorkOrder && workOrder ? {
		WorkOrderTemplateId: "new",
		Title: workOrder.Title,
		Description: workOrder.Description,
		Priority: workOrder.Priority,
		Location: workOrder.Location,
		Categories: workOrder.Categories,
		Assets: workOrder.Assets,
		Vendors: workOrder.Vendors,
		AssignedTo: workOrder.AssignedTo,
		DueDays: undefined,
		Created: workOrder.Created,
		CreatedBy: workOrder.CreatedBy,
		UpdatedBy: null,
		DeletedBy: null
	} : null;

	useEffect(() => {
		if (templateIdFromParams) {
			const template = templates?.find((t: ListWorkOrderTemplateDto) => t.WorkOrderTemplateId === templateIdFromParams);
			if (template) {
				setSelectedTemplate?.(template);
				if (template) {
					setSelectedTemplate?.(template);
				}
			}
		} else if (templates && templates.length > 0) {
			setSelectedTemplate?.(templates[0]);
		}
	}, [templateIdFromParams, templates, isMobile]);

	useEffect(() => {
		if (!templateIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleEdit = () => {
		navigate(`/workordertemplates/${template?.WorkOrderTemplateId}/edit`);
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedTemplate(null);
		navigate(`/workordertemplates/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleTemplateSelect = (template: ListWorkOrderTemplateDto | null) => {
		if (!template)
			return;

		setRequestedTemplateId(template.WorkOrderTemplateId);

		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}

		setSelectedTemplate(template);
		navigate(`/workordertemplates/${template.WorkOrderTemplateId}`);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (values: MutableWorkOrderTemplateDto) => {
		if (isNew) {
			createTemplate(values, {
				onSuccess: (response: any) => {
					setIsChanged(false);
					navigate(`/workordertemplates/${response.data.WorkOrderTemplateId}`, { replace: true });
				}
			});
		} else {
			updateTemplate(values, {
				onSuccess: () => {
					setIsChanged(false);
					navigate(-1);
				}
			});
		}
	}

	const handleDelete = () => {
		if (!template) return;

		closeDeleteModal();
		deleteTemplate(template, {
			onSuccess: () => {
				setSelectedTemplate(null);
				navigate(`/workordertemplates`, { replace: true });
			}
		});
	}

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			setIsChanged(false);

			navigate(-1);

			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		setIsChanged(false);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}

		if (requestedTemplateId && requestedTemplateId !== template?.WorkOrderTemplateId) {
			navigate(`/workordertemplates/${requestedTemplateId}`);
			setRequestedTemplateId(null);
		} else {
			navigate(-1);
		}
	}

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<DeleteTemplateModal
			template={template}
			open={deleteModalOpen}
			onDelete={handleDelete}
			onClose={closeDeleteModal}
		/>

		<DiscardChangesModal
			open={discardChangesModalOpened}
			onDiscard={handleDiscardChanges}
			onClose={handleCancelDiscard}
		/>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditing || isNew ? <>
				{!isNewFromWorkOrder ? (
					<WorkOrderTemplateEditView template={!isNew ? template : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				) : ( 
					<WorkOrderTemplateEditView template={workOrderTemplate} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				)}
			</> : (
				<WorkOrderTemplateDetailView template={template} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}	
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Work Order Templates</Title>
			<RoleTemplate role={AzureADRoles.Admin}>
				<Button onClick={handleNew} radius="xl" leftSection={<IconPlus />}>New Template</Button>
			</RoleTemplate>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!templates || templates.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<WorkOrderTemplateList templates={templates} isLoading={isLoadingTemplates} selectedTemplate={selectedTemplate} setSelectedTemplate={handleTemplateSelect} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditing || isNew ? <>
					{!isNewFromWorkOrder ? (
						<WorkOrderTemplateEditView template={!isNew ? template : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
					) : ( 
						<WorkOrderTemplateEditView template={workOrderTemplate} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
					)}
				</> : templates && templates.length > 0 ? (
					<WorkOrderTemplateDetailView template={template} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>
}

export default WorkOrderTemplatesPage

const DiscardChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you'll lose unsaved changes to this template.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteTemplateModal = ({ template, open, onDelete, onClose }: { template?: WorkOrderTemplateDto, open: boolean, onDelete: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete Template</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete this template?</Text>
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
